import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as ExistingWorkSpace } from "../../images/Frame.svg";
import { ReactComponent as NewWorkSpace } from "../../images/Frame2.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const PageTitleModal = ({
  importToolAvailable,
  onSave,
  taskType,
  pageTitle,
  setPageTitle,
  saveLoading,
  workspaceChoice,
  setWorkspaceChoice,
  existingChoice,
  setExistingChoice,
  setPreviousSelection,
  setPageTitleModalOpen,
  setConfluencePageOpen,
  pageTitleModalOpen,
}) => {
  const handleOk = () => {
    onSave();
    setPageTitleModalOpen(false);
  };

  const handleCloseModal = () => {
    setPageTitleModalOpen(false);
    setConfluencePageOpen(false);
  };

  const handleBack = () => {
    setPreviousSelection(null);
  };
  return (
    <Drawer
      anchor="right"
      open={pageTitleModalOpen}
      onClose={handleCloseModal}
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "90vw", // mobile
              sm: "70vw", // tablet
              md: "50vw", // small laptops
              lg: "40vw", // desktop
            },
            maxWidth: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArrowBackIosNewIcon
              fontSize="small"
              onClick={handleBack}
              sx={{ mr: 1, cursor: "pointer" }}
            />
            <Typography variant="h6">{taskType} Page</Typography>
          </Box>

          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Workspace Choice */}
        <Box
          mt={3}
          display="flex"
          gap={2}
          flexDirection={{ xs: "column", sm: "row" }} // stack on mobile
        >
          {/* New Workspace */}
          <Box
            onClick={() => setWorkspaceChoice("new")}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 2,
              border: "2px solid",
              borderColor: workspaceChoice === "new" ? "#0094FB" : "grey.300",
              bgcolor: workspaceChoice === "new" ? "#E1F5FF" : "transparent",
              cursor: "pointer",
            }}
          >
            <NewWorkSpace style={{ width: 24, height: 24, marginBottom: 8 }} />
            <Typography
              variant="subtitle1"
              fontWeight="500"
              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
            >
              New Workspace
            </Typography>
          </Box>

          {/* Existing Workspace */}
          {importToolAvailable && (
            <Box
              onClick={() => setWorkspaceChoice("existing")}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: "2px solid",
                borderColor:
                  workspaceChoice === "existing" ? "#0094FB" : "grey.300",
                bgcolor:
                  workspaceChoice === "existing" ? "#E1F5FF" : "transparent",
                cursor: "pointer",
              }}
            >
              <ExistingWorkSpace
                style={{ width: 24, height: 24, marginBottom: 8 }}
              />
              <Typography
                variant="subtitle1"
                fontWeight="500"
                sx={{ fontSize: { xs: "14px", sm: "16px" } }}
              >
                Existing Workspace
              </Typography>
            </Box>
          )}
        </Box>

        {/* New Workspace Input */}
        {workspaceChoice === "new" && (
          <Box
            mt={3}
            sx={{
              backgroundColor: "#F8FBFF",
              borderRadius: "8px",
              p: 2,
            }}
          >
            <TextField
              label="Page Title"
              placeholder="Enter Page Title"
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  backgroundColor: "#F8FBFF",
                },
              }}
            />
          </Box>
        )}

        {/* Existing Workspace Options */}
        {workspaceChoice === "existing" && (
          <Box
            mt={3}
            sx={{
              backgroundColor: "#F8FBFF",
              borderRadius: "8px",
              p: { xs: 2, sm: 3 },
            }}
          >
            <Typography sx={{ fontSize: "20px", fontWeight: 600, mb: 2 }}>
              Save to Existing Workspace
            </Typography>

            <RadioGroup
              value={existingChoice}
              onChange={(e) => setExistingChoice(e.target.value)}
            >
              {/* Overwrite Option */}
              <FormControlLabel
                value="overwrite"
                control={<Radio />}
                sx={{ alignItems: "flex-start", mb: 1 }}
                label={
                  <Box>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                      Overwrite the existing page
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "text.secondary",
                        mt: 0.3,
                        lineHeight: 1.5,
                      }}
                    >
                      This will completely remove the old data and replace the
                      new data
                    </Typography>
                  </Box>
                }
              />

              {/* Add New Page Option */}
              <FormControlLabel
                value="addnew"
                control={<Radio />}
                sx={{ alignItems: "flex-start" }}
                label={
                  <Box>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                      Add a new page
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "text.secondary",
                        mt: 0.3,
                        lineHeight: 1.5,
                      }}
                    >
                      Create a new page to add to your project
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

            {/* Page Title Field for Add New */}
            {existingChoice === "addnew" && (
              <TextField
                label="New Page Name"
                placeholder="New Page"
                fullWidth
                sx={{ mt: 2 }}
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
              />
            )}
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: "auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: 2,
            pt: "8px",
            backgroundColor: "#ffffff",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleCloseModal}
            sx={{
              width: { xs: "100%", sm: "60px", md: "80px" },
              textTransform: "none",
              borderRadius: "8px",
              borderWidth: "2px",
              borderColor: "#0094FB",
              "&:hover": { backgroundColor: "#ffffff" },
              color: "#0094FB",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small" // make button smaller
            onClick={handleOk}
            disabled={
              saveLoading ||
              !workspaceChoice ||
              ((workspaceChoice === "new" ||
                (workspaceChoice === "existing" &&
                  existingChoice === "addnew")) &&
                !pageTitle)
            }
            sx={{
              width: { xs: "100%", sm: "60px", md: "80px" },
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#0094FB",
              "&:hover": { backgroundColor: "#0094FB" },
              "&.Mui-disabled": {
                backgroundColor: "#0094FB",
                color: "#fff",
                opacity: 0.6,
              },
            }}
          >
            {saveLoading ? <CircularProgress size={20} /> : "Confirm"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PageTitleModal;
